import MuiChip, { ChipProps as Props } from '@material-ui/core/Chip'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import * as React from 'react'

import Color from '../base/Color'

export type CustomVariant =
  | 'primary'
  | 'secondary'
  | 'officeCleaning'
  | 'homeCleaning'
  | 'percentage'
  | 'hourlyRate'
  | 'casual'
  | 'error'
  | 'assigned'
  | 'changed'
  | 'unassigned'
  | 'replaced'
  | 'unpaid'
  | 'paid'
  | 'underpaid'
  | 'stripeProcessing'
  | 'autocomplete'

export type SizeTags = 'xs' | 'small' | 'medium' | 'large'

export interface TagInterface {
  label?: string
  variant?: CustomVariant
}

export type TagProps = {
  customVariant?: CustomVariant
  sizes?: SizeTags
} & Props

const typography = {
  xsMedium: {
    fontSize: 13,
    lineHeight: 1.38,
    fontWeight: 500,
    alignItems: 'center'
  },
  xxsBold: {
    fontSize: 11,
    lineHeight: 1.45,
    fontWeight: 700,
    alignItems: 'center'
  },
  tagsXs: {
    fontWeight: 700,
    fontSize: 8,
    lineHeight: 1.5,
    alignItems: 'center'
  }
}

const LabelStyle = {
  '& .MuiChip-label': {
    padding: 0
  },
  '& .MuiChip-deleteIcon': {
    display: 'none'
  }
}

const XS = {
  height: 'auto',
  padding: '3px 6px',
  borderRadius: 2,
  ...typography.tagsXs
}
const Small = {
  height: 'auto',
  padding: '3px 12px',
  borderRadius: 4,
  ...typography.xxsBold
}
const Medium = {
  height: 'auto',
  padding: '6px 12px',
  borderRadius: 4,
  ...typography.xxsBold
}
const Large = {
  height: 'auto',
  padding: '7px 16px',
  borderRadius: 4,
  ...typography.xsMedium
}

const getSize = (sizes: string) => {
  if (sizes === 'xs') {
    return XS
  } else if (sizes === 'small') {
    return Small
  } else if (sizes === 'medium') {
    return Medium
  } else if (sizes === 'large') {
    return Large
  } else {
    return Medium
  }
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    primary: (props: TagProps) => ({
      background: Color.base.firstBase,
      color: theme.palette.common.white,
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    secondary: (props: TagProps) => ({
      background: Color.primary[400],
      color: theme.palette.primary.main,
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    officeCleaning: (props: TagProps) => ({
      background: Color.base.secondBlue,
      color: theme.palette.common.white,
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    homeCleaning: (props: TagProps) => ({
      background: Color.base.secondGreen,
      color: theme.palette.common.white,
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    percentage: (props: TagProps) => ({
      background: Color.base.firstTint,
      color: Color.primary[800],
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    hourlyRate: (props: TagProps) => ({
      background: Color.primary[700],
      color: theme.palette.common.white,
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    casual: (props: TagProps) => ({
      background: theme.palette.grey[800],
      color: theme.palette.common.white,
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    error: (props: TagProps) => ({
      background: theme.palette.error.main,
      color: theme.palette.common.white,
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    assigned: (props: TagProps) => ({
      background: Color.base.thirdGreen,
      color: theme.palette.common.white,
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    changed: (props: TagProps) => ({
      background: Color.base.secondPurple,
      color: theme.palette.common.white,
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    unassigned: (props: TagProps) => ({
      background: Color.base.thirdBase,
      color: theme.palette.common.white,
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    replaced: (props: TagProps) => ({
      background: Color.base.secondAdditional,
      color: theme.palette.common.white,
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    paid: (props: TagProps) => ({
      background: Color.base.secondTint,
      color: theme.palette.success.main,
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    unpaid: (props: TagProps) => ({
      background: Color.base.thirdTint,
      color: Color.base.thirdBase,
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    stripeProcessing: (props: TagProps) => ({
      background: theme.palette.grey[300],
      color: theme.palette.grey[700],
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    underpaid: (props: TagProps) => ({
      background: Color.primary[300],
      color: Color.primary[700],
      ...LabelStyle,
      ...getSize(props.sizes ? props.sizes : 'medium')
    }),
    autocomplete: (props: TagProps) => ({
      background: theme.palette.grey[500],
      color: theme.palette.common.black,
      '& .MuiChip-label': {
        padding: 0
      },
      '& .MuiChip-deleteIcon': {
        width: 16,
        height: 16,
        marginRight: 0,
        marginLeft: 4
      },
      padding: '4px 8px',
      borderRadius: '4px'
    })
  })
)

export const TagsChip: React.FC<TagProps> = (props) => {
  const classes = useStyles(props)
  const { label, customVariant, ...rest } = props
  const getClasses = (variant: string) => {
    if (customVariant === undefined) {
      return { root: classes.primary }
    } else if (customVariant === variant) {
      return { root: classes[variant] }
    }
  }
  return (
    <MuiChip
      {...rest}
      classes={getClasses(customVariant as string)}
      label={label}
    />
  )
}

export default TagsChip
